'use client';

import { useEffect, useRef } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useExpandableContent } from '@/hooks/use-expandable-content';
import { cn } from '@/lib/utils';

import ReadMoreBtn from './read-more-btn';

type ExpandableContentProps = {
  content: string;
  className?: string;
  paragraphClassName?: string;
  buttonClassName?: string;
  buttonPosition?: 'left' | 'right';
  collapseOnBreakpoint?: boolean;
};

export function ExpandableContent({
  content,
  className,
  paragraphClassName,
  buttonClassName,
  buttonPosition = 'right',
  collapseOnBreakpoint = false,
}: ExpandableContentProps) {
  const {
    isExpanded,
    setIsExpanded,
    isLoaded,
    isMobile,
    paragraphs,
    firstParagraph,
    hasMoreContent,
    toggleExpand,
  } = useExpandableContent(content);

  const prevMobileRef = useRef(isMobile);

  // Collapse content only when mobile breakpoint changes
  useEffect(() => {
    if (
      collapseOnBreakpoint &&
      prevMobileRef.current !== isMobile &&
      isExpanded
    ) {
      setIsExpanded(false);
    }
    prevMobileRef.current = isMobile;
  }, [isMobile, collapseOnBreakpoint, setIsExpanded, isExpanded]);

  return (
    <div className={cn('text-base text-brand-dark md:text-xl', className)}>
      <p>{firstParagraph}</p>

      {/* Desktop: always show all paragraphs */}
      <div className='mt-4 hidden md:block'>
        {paragraphs.slice(1).map((paragraph, index) => (
          <p key={index} className={cn('mt-4', paragraphClassName)}>
            {paragraph}
          </p>
        ))}
      </div>

      {/* Mobile: show additional paragraphs when expanded */}
      {isMobile && isExpanded && hasMoreContent && (
        <div className='mt-4 block md:hidden'>
          {paragraphs.slice(1).map((paragraph, index) => (
            <p key={index} className={cn('mt-4', paragraphClassName)}>
              {paragraph}
            </p>
          ))}
        </div>
      )}

      {/* Button area with fixed height to prevent layout shift */}
      <div
        className={`mt-4 flex h-10 md:hidden ${
          buttonPosition === 'right' ? 'justify-end' : 'justify-start'
        }`}
      >
        {!isLoaded && hasMoreContent ? (
          <Skeleton className='h-6 w-24 pt-3' />
        ) : (
          hasMoreContent && (
            <ReadMoreBtn
              onClick={toggleExpand}
              isExpanded={isExpanded}
              className={buttonClassName}
            />
          )
        )}
      </div>
    </div>
  );
}
